<template>
  <div class="assignees" :class="`assignees--${size}`" @click.stop>
    <div v-for="user in visibleAssignees" :key="user.id" class="assignees__avatar-wrap">
      <v-tooltip location="top" :text="fullName(user)">
        <template #activator="{ props: tipProps }">
          <span
            v-bind="tipProps"
            class="assignees__avatar"
            :class="`assignees__avatar--${size}`"
            :style="avatarStyle(user)"
            >{{ initialsOf(user) }}</span
          >
        </template>
      </v-tooltip>
      <button
        type="button"
        class="assignees__remove"
        :aria-label="`Unassign ${fullName(user)}`"
        @click.stop="onToggle(user.id, false)">
        <v-icon size="9">mdi-close</v-icon>
      </button>
    </div>
    <v-tooltip v-if="overflowCount > 0" location="top" :text="overflowNames">
      <template #activator="{ props: tipProps }">
        <span
          v-bind="tipProps"
          class="assignees__avatar assignees__avatar--overflow"
          :class="`assignees__avatar--${size}`"
          >+{{ overflowCount }}</span
        >
      </template>
    </v-tooltip>
    <v-menu :close-on-content-click="false" location="bottom start" @update:model-value="onMenuToggle">
      <template #activator="{ props: menuProps }">
        <button
          v-bind="menuProps"
          type="button"
          class="assignees__add"
          :class="`assignees__add--${size}`"
          aria-label="Assign teammate"
          @click.stop>
          <v-icon :size="size === 'md' ? 14 : 15">mdi-account-plus-outline</v-icon>
          <span v-if="size === 'md'">Assign</span>
        </button>
      </template>
      <v-card min-width="250" class="assignees__menu">
        <v-card-text class="pa-2">
          <div
            v-for="candidate in candidates"
            :key="candidate.id"
            class="assignees__item"
            @click.stop="onToggle(candidate.id, !assignedIds.has(candidate.id))">
            <span class="assignees__avatar assignees__avatar--sm" :style="avatarStyle(candidate)">{{
              initialsOf(candidate)
            }}</span>
            <span class="assignees__item-label">{{ candidate.isMe ? "Me" : fullName(candidate) }}</span>
            <v-icon v-if="assignedIds.has(candidate.id)" size="16" color="accent">mdi-check</v-icon>
          </div>
          <div v-if="!candidates.length" class="assignees__empty">No teammates yet</div>
        </v-card-text>
      </v-card>
    </v-menu>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { TAG_COLORS } from "@/constants/tagColors";
import { assignmentsService } from "@/services/assignmentsService";
import { useAuthStore, useInboxStore } from "@/stores";

const AVATAR_COLORS = Object.values(TAG_COLORS);

const props = defineProps({
  assignees: { type: Array, default: () => [] },
  entityType: { type: String, required: true, validator: (v) => ["document", "order", "submission"].includes(v) },
  entityId: { type: [String, Number], required: true },
  size: { type: String, default: "sm", validator: (v) => ["sm", "md"].includes(v) },
});

const emit = defineEmits(["update:assignees"]);

const authStore = useAuthStore();
const inboxStore = useInboxStore();

// Grid cells cap at 3 bubbles so the "+N" chip always fits inline in the
// fixed-width column; free-width surfaces (detail bars, mobile cards) show 4.
const maxVisible = computed(() => (props.size === "sm" ? 3 : 4));
const assignedIds = computed(() => new Set((props.assignees || []).map((a) => a.id)));
const visibleAssignees = computed(() => (props.assignees || []).slice(0, maxVisible.value));
const overflowCount = computed(() => Math.max(0, (props.assignees || []).length - maxVisible.value));
const overflowNames = computed(() =>
  (props.assignees || [])
    .slice(maxVisible.value)
    .map((u) => fullName(u))
    .join(", ")
);

const me = computed(() => {
  const info = authStore.userInfo || {};
  if (info.pk == null) return null;
  return { id: info.pk, firstName: info.firstName, lastName: info.lastName, email: info.email, isMe: true };
});

const candidates = computed(() => {
  const teammates = (inboxStore.teammates || []).map((t) => ({ ...t, isMe: false }));
  return me.value ? [me.value, ...teammates] : teammates;
});

function onMenuToggle(open) {
  if (open) inboxStore.fetchTeammates().catch(() => {});
}

function fullName(user) {
  return `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || "Unknown user";
}

function initialsOf(user) {
  const initials = `${(user.firstName || "").charAt(0)}${(user.lastName || "").charAt(0)}`.toUpperCase();
  return initials || (user.email || "?").charAt(0).toUpperCase();
}

function avatarStyle(user) {
  const color = AVATAR_COLORS[user.id % AVATAR_COLORS.length] || TAG_COLORS.grey;
  return { background: `color-mix(in srgb, ${color} 16%, white)`, color };
}

async function onToggle(userId, assign) {
  const previous = props.assignees || [];
  let next;
  if (assign) {
    const user = candidates.value.find((c) => c.id === userId);
    if (!user || assignedIds.value.has(userId)) return;
    next = [...previous, { id: user.id, firstName: user.firstName, lastName: user.lastName, email: user.email }];
  } else {
    next = previous.filter((a) => a.id !== userId);
  }
  emit("update:assignees", next);
  try {
    const { assignees } = await assignmentsService.setAssignees(
      props.entityType,
      props.entityId,
      next.map((a) => a.id)
    );
    emit("update:assignees", assignees);
  } catch {
    emit("update:assignees", previous);
  }
}
</script>

<style lang="scss" scoped>
// Grid cells (sm) stay on ONE line by construction: 22px bubbles overlapped
// -8px mean even "3 + overflow + add" tops out around 100px inside the 140px
// column. Only the free-width md variant (detail bars, mobile cards) wraps.
.assignees {
  display: inline-flex;
  align-items: center;

  &--md {
    flex-wrap: wrap;
    row-gap: 6px;
  }
}

.assignees__add {
  white-space: nowrap;
  flex-shrink: 0;
}

.assignees__avatar-wrap {
  position: relative;

  &:not(:first-child) {
    margin-left: -5px;
  }

  &:hover {
    z-index: 2;

    .assignees__remove {
      opacity: 1;
    }
  }
}

.assignees__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-family: $lh_font_family_dm_sans;
  font-weight: 700;
  letter-spacing: 0.02em;
  flex-shrink: 0;
  box-shadow: 0 0 0 2px #fff;

  &--sm {
    width: 26px;
    height: 26px;
    font-size: 10.5px;
  }

  &--md {
    width: 30px;
    height: 30px;
    font-size: 12px;
  }

  &--overflow {
    background: $lh_color_snowy_white;
    color: $lh_color_blue_gray;
    margin-left: -5px;
  }
}

.assignees__remove {
  position: absolute;
  top: -3px;
  right: -3px;
  width: 13px;
  height: 13px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: $lh_color_navy;
  color: #fff;
  cursor: pointer;
  opacity: 0;
  transition: opacity $lh_transition_fast;
  z-index: 3;
}

.assignees__add {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 1px dashed rgba($lh_color_navy, 0.25);
  background: transparent;
  color: $lh_color_blue_gray;
  font-family: $lh_font_family_dm_sans;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: color $lh_transition_fast, border-color $lh_transition_fast, background $lh_transition_fast;

  &:hover {
    color: $lh_color_blue;
    border-color: rgba($lh_color_blue, 0.45);
    background: rgba($lh_color_blue, 0.04);
  }

  &--sm {
    width: 26px;
    height: 26px;
    padding: 0;
    flex: 0 0 auto;
    border-radius: 50%;
    margin-left: 7px;
  }

  &--md {
    height: 30px;
    padding: 0 12px 0 9px;
    border-radius: 15px;
    margin-left: 8px;
  }
}

.assignees__menu {
  max-height: 320px;
  overflow-y: auto;
  border-radius: 12px !important;
}

.assignees__item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-family: $lh_font_family_dm_sans;
  transition: background $lh_transition_fast;

  &:hover {
    background: $lh_color_snowy_white;
  }
}

.assignees__item-label {
  font-size: 13.5px;
  font-weight: 500;
  color: $lh_color_navy;
  flex: 1 1 auto;
}

.assignees__empty {
  padding: 10px;
  font-size: 13px;
  color: $lh_color_blue_gray;
}
</style>
